import React, { useState, useContext, useEffect, useRef } from "react";
import VisionRoyaleLogo from "../media/VisionRoyale_logo.png";
import {
  FaHome,
  FaHistory,
  FaProjectDiagram,
  FaFutbol,
  FaUser,
  FaBars,
  FaTimes,
  FaUserShield,
  FaEnvelope,
  FaCrown,
  FaClock,
  FaBolt,
  FaStar,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
const getTierIcon = (tier) => {
  switch (tier) {
    case "trial":
      return <FaClock className="text-yellow-400 text-xs" />;
    case "basic":
      return <FaBolt className="text-blue-400 text-xs" />;
    case "pro":
      return <FaStar className="text-green-400 text-xs" />;
    case "premium":
      return <FaCrown className="text-yellow-400 text-xs" />;
    default:
      return null;
  }
};
const getTierDisplay = (tier) => {
  const tiers = {
    trial: {
      name: "Free Trial",
      color: "text-yellow-400",
      bg: "bg-yellow-500/20",
      icon: <FaClock className="text-yellow-400" />,
      border: "border-yellow-500/30",
    },
    basic: {
      name: "Basic Member",
      color: "text-blue-400",
      bg: "bg-blue-500/20",
      icon: <FaBolt className="text-blue-400" />,
      border: "border-blue-500/30",
    },
    pro: {
      name: "Pro Member",
      color: "text-green-400",
      bg: "bg-green-500/20",
      icon: <FaStar className="text-green-400" />,
      border: "border-green-500/30",
    },
    premium: {
      name: "Premium Member",
      color: "text-purple-400",
      bg: "bg-purple-500/20",
      icon: <FaCrown className="text-yellow-400" />,
      border: "border-purple-500/30",
    },
  };
  return (
    tiers[tier] || {
      name: tier,
      color: "text-gray-400",
      bg: "bg-gray-500/20",
      icon: null,
      border: "border-gray-500/30",
    }
  );
};
// Componenta Eye animata
const Eye = () => {
  const eyeRef = useRef();
  const pupilRef = useRef();
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!eyeRef.current || !pupilRef.current) return;
      const rect = eyeRef.current.getBoundingClientRect();
      const eyeCenterX = rect.left + rect.width / 2;
      const eyeCenterY = rect.top + rect.height / 2;
      const dx = e.clientX - eyeCenterX;
      const dy = e.clientY - eyeCenterY;
      const angle = Math.atan2(dy, dx);
      const radius = 4; // pupila nu iese din ochi
      pupilRef.current.style.transform = `translate(${Math.cos(angle) * radius}px, ${Math.sin(angle) * radius}px)`;
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return (
    <div
      ref={eyeRef}
      className="relative w-6 h-6 rounded-full bg-white flex items-center justify-center"
    >
      <div
        ref={pupilRef}
        className="w-3 h-3 rounded-full bg-black transition-transform duration-100"
      />
    </div>
  );
};
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  };
  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    navigate("/login");
  };
  const isActive = (path) => location.pathname === path;
  const navItems = [
    { path: "/", icon: <FaHome />, label: "Home" },
    { path: "/history", icon: <FaHistory />, label: "History" },
    { path: "/projects", icon: <FaProjectDiagram />, label: "Projects" },
    { path: "/matches", icon: <FaFutbol />, label: "Matches" },
    { path: "/premium", icon: <FaCrown />, label: "Premium" },
  ];
  const adminItem = user?.is_admin
    ? { path: "/admin", icon: <FaUserShield />, label: "Admin" }
    : null;
  const getActiveButtonClass = () =>
    "bg-gradient-to-r from-green-900/30 to-emerald-900/20 border-green-500/60 text-green-300 shadow-[0_0_25px_rgba(52,211,153,0.6)]";
  const getNormalButtonClass = () =>
    "bg-gray-900/40 border-gray-800 text-gray-400";
  const getHoverClass = () =>
    "hover:border-green-500 hover:shadow-[0_0_25px_rgba(52,211,153,0.5)] hover:text-green-300";
  const topBorderGradient =
    "from-transparent via-green-300/70 via-30% via-emerald-400 via-50% via-cyan-400 via-70% to-transparent";
  const bottomBorderGradient =
    "from-transparent via-cyan-400 via-30% via-emerald-400 via-50% via-green-300/70 via-70% to-transparent";
  const sideBorderGradient =
    "from-transparent via-green-300/60 via-25% via-emerald-400/80 via-40% via-cyan-300/70 via-60% via-emerald-400/80 via-75% via-green-300/60 to-transparent";
  return (
    <>
      {/* Top border */}
      <div
        className={`fixed top-0 left-0 w-full h-1 bg-gradient-to-r ${topBorderGradient} animate-pulse z-50`}
      />
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrollY > 50
            ? "bg-gray-900/95 backdrop-blur-xl border border-gray-800/50 shadow-2xl"
            : "bg-gray-900/90 backdrop-blur-lg border border-gray-800/30"
        }`}
      >
        {/* Left & right borders */}
        <div
          className={`absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b ${sideBorderGradient} animate-pulse`}
        />
        <div
          className={`absolute top-0 right-0 w-1.5 h-full bg-gradient-to-b ${sideBorderGradient} animate-pulse`}
        />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="flex items-center justify-between h-20">
            {/* Left logo + branding */}
            <div className="flex items-center lg:-ml-12 mr-6 lg:mr-6 flex-1 lg:flex-none justify-center lg:justify-start">
              <button
                onClick={() => navigate("/")}
                className="flex items-center space-x-5 group relative"
              >
                <div className="relative">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden transform group-hover:scale-110 transition-all duration-300">
                    <img
                      src={VisionRoyaleLogo}
                      alt="Vision Royale"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                {/* Text branding + Eye */}
                <div className="flex flex-col justify-center leading-none ml-2">
                  <div className="flex items-center gap-1">
                    <h1 className="text-2xl font-extrabold bg-gradient-to-r from-emerald-400 via-green-300 to-cyan-300 bg-clip-text text-transparent">
                      Visi
                    </h1>
                    <Eye />
                    <h1 className="text-2xl font-extrabold bg-gradient-to-r from-emerald-400 via-green-300 to-cyan-300 bg-clip-text text-transparent">
                      n
                    </h1>
                  </div>
                  <h1 className="text-2xl font-extrabold bg-gradient-to-r from-emerald-400 via-green-300 to-cyan-300 bg-clip-text text-transparent -mt-1">
                    Royale
                  </h1>
                </div>
              </button>
            </div>
            {/* Nav items */}
            <nav className="hidden lg:flex items-center space-x-2 flex-1 justify-center">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className="relative group"
                >
                  <div
                    className={`flex items-center space-x-3 px-5 py-3 rounded-xl border transition-all duration-300 transform group-hover:-translate-y-0.5 ${
                      isActive(item.path)
                        ? `${getActiveButtonClass()} scale-105`
                        : `${getNormalButtonClass()} ${getHoverClass()}`
                    }`}
                  >
                    <span
                      className={`text-lg transition-transform duration-300 ${
                        isActive(item.path)
                          ? "scale-125 animate-pulse"
                          : "group-hover:scale-125"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span className="font-semibold tracking-wide">
                      {item.label}
                    </span>
                  </div>
                </button>
              ))}
              {adminItem && (
                <button
                  onClick={() => handleNavigation(adminItem.path)}
                  className="relative group"
                >
                  <div
                    className={`flex items-center space-x-3 px-5 py-3 rounded-xl border transition-all duration-300 transform group-hover:-translate-y-0.5 ${
                      isActive(adminItem.path)
                        ? `${getActiveButtonClass()} scale-105`
                        : `${getNormalButtonClass()} ${getHoverClass()}`
                    }`}
                  >
                    <span className="text-lg">{adminItem.icon}</span>
                    <span className="font-semibold tracking-wide">
                      {adminItem.label}
                    </span>
                  </div>
                </button>
              )}
            </nav>
            <div className="flex items-center space-x-4">
              <div className="relative mr-4">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-3 rounded-xl -mr-7 hover:bg-gray-800/50 transition-all duration-300 group"
                >
                  <div className="relative">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-gray-700 shadow-md ${getTierDisplay(user?.subscription?.tier || "trial").bg}`}
                    >
                      <span
                        className={`font-bold ${getTierDisplay(user?.subscription?.tier || "trial").color}`}
                      >
                        {user?.username?.charAt(0)?.toUpperCase() || "V"}
                      </span>
                    </div>
                    {/* Iconul pentru tier în colț pentru toate tier-urile (inclusiv trial) */}
                    {user?.subscription?.tier && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-gray-900 to-black rounded-full flex items-center justify-center border border-gray-700 p-0.5">
                        {getTierIcon(user.subscription.tier)}
                      </div>
                    )}
                    <div className="absolute -inset-1 border-2 border-gradient-to-r from-green-400/30 via-emerald-400/40 to-cyan-400/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                  <div className="hidden xl:block text-left">
                    <div className="flex items-center space-x-2">
                      <p
                        className={`text-sm font-bold ${getTierDisplay(user?.subscription?.tier || "trial").color}`}
                      >
                        {user?.username || "User"}
                      </p>
                    </div>
                    {user?.subscription?.tier && (
                      <div className="flex items-center space-x-1 mt-1">
                        <p
                          className={`text-xs ${getTierDisplay(user.subscription.tier).color}`}
                        >
                          {getTierDisplay(user.subscription.tier).name}
                        </p>
                      </div>
                    )}
                  </div>
                  <div
                    className={`hidden lg:block transition-transform duration-300 ${
                      isProfileOpen ? "rotate-180" : ""
                    }`}
                  >
                    <div className="w-5 h-5 bg-gray-800 rounded-lg flex items-center justify-center">
                      <div className="w-1.5 h-1.5 border-r-2 border-b-2 border-gray-400 transform rotate-45 -translate-y-0.5"></div>
                    </div>
                  </div>
                </button>
                {isProfileOpen && (
                  <>
                    {/* Overlay */}
                    <div
                      className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
                      onClick={() => setIsProfileOpen(false)}
                    />
                    {/* Card */}
                    <div className="absolute right-0 mt-3 w-64 bg-gray-900/95 backdrop-blur-xl border border-gray-700/30 rounded-xl shadow-2xl z-50 overflow-hidden">
                      {/* My Account Card */}
                      <div className="p-4 bg-gray-900/50 border-b border-gray-700/30 relative rounded-t-xl">
                        {/* Avatar + Username */}
                        <div className="flex items-start space-x-6 w-full relative min-h-16">
                          {/* Avatar - left side */}
                          <div
                            className={`w-16 h-16 rounded-full flex items-center justify-center border-2 border-gray-700 shadow-md flex-shrink-0
                ${getTierDisplay(user?.subscription?.tier || "trial").bg}`}
                          >
                            <span
                              className={`font-bold text-lg ${getTierDisplay(user?.subscription?.tier || "trial").color}`}
                            >
                              {user?.username?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                          </div>
                          {/* Username wave */}
                          <div className="flex flex-wrap gap-0.5 content-start relative">
                          {user?.username?.split("").map((char, index) => {
                            const textSize = user?.username?.length > 10 ? "text-sm" : "text-2xl";
                            return (
                              <span
                                key={index}
                                className={`font-extrabold ${textSize} inline-block relative ${getTierDisplay(user?.subscription?.tier || "trial").color}`}
                                style={{
                                  animation: `wave 1.2s linear ${index * 0.1}s infinite`,
                                }}
                              >
                                {char}
                              </span>
                            );
                          })}
                        </div>
                          {/* Subscription badge rotund - bottom right corner */}
                          <div className="absolute bottom-0 right-0 z-20">
                            <div
                              className={`text-xs font-semibold px-2 py-1 rounded-full shadow-sm flex items-center gap-1
                ${getTierDisplay(user?.subscription?.tier || "trial").bg}
                ${getTierDisplay(user?.subscription?.tier || "trial").border}
                ${getTierDisplay(user?.subscription?.tier || "trial").color}`}
                            >
                              {getTierIcon(user?.subscription?.tier || "trial")}
                              <span>
                                {
                                  getTierDisplay(
                                    user?.subscription?.tier || "trial",
                                  ).name
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Menu items */}
                      <div className="p-2 bg-transparent">
                        {[
                          {
                            icon: <FaUser />,
                            label: "My Profile",
                            path: "/profile",
                          },
                          {
                            icon: <FaCrown />,
                            label: "Subscription Plans",
                            path: "/subscription-plans",
                          },
                          {
                            icon: <FaEnvelope />,
                            label: "Contact",
                            path: "/contact",
                          },
                        ].map((item, index) => (
                          <button
                            key={index}
                            onClick={() =>
                              item.path &&
                              !(
                                user.username === "test" &&
                                item.path === "/subscription-plans"
                              ) &&
                              handleNavigation(item.path)
                            }
                            className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition-all duration-200 group relative mb-0.5 ${getNormalButtonClass()} ${getHoverClass()}`}
                          >
                            <span className="text-lg transition-transform duration-300 group-hover:scale-125">
                              {item.icon}
                            </span>
                            <span className="font-medium relative z-10 flex-1 text-left">
                              {item.label}
                            </span>
                          </button>
                        ))}
                      </div>
                      {/* Logout */}
                      <div className="p-2 border-t border-gray-700/30 bg-transparent">
                        <button
                          onClick={handleLogout}
                          className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 font-medium text-sm group ${getNormalButtonClass()} ${getHoverClass()}`}
                        >
                          <span>Sign Out</span>
                        </button>
                      </div>
                      {/* Inline animations */}
                      <style>
                        {`
          @keyframes explodeLetter {
            0%,100% { transform: translate(0,0) rotate(0deg) scale(1); opacity:1; }
            10% { transform: translate(var(--x), var(--y)) rotate(var(--rotate)) scale(1.5); opacity:0.7; }
            40% { transform: translate(calc(var(--x)/2), calc(var(--y)/2)) rotate(calc(var(--rotate)/2)) scale(1.2); opacity:0.85; }
            60%, 90% { transform: translate(0,0) rotate(0deg) scale(1); opacity:1; } 
          }
          @keyframes sparkGlow {
            0%,100% { opacity:0; }
            50% { opacity:0.6; text-shadow: 0 0 8px currentColor, 0 0 12px currentColor; }
          }
          @keyframes wave {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
          .animate-sparkGlow { animation: sparkGlow 6s ease-in-out infinite; }
        `}
                      </style>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-3 rounded-xl bg-gray-900/50 border border-gray-800 hover:border-gradient-to-r hover:from-green-500/50 hover:via-emerald-500/50 hover:to-cyan-500/50 hover:text-green-400 transition-colors"
              >
                {isMenuOpen ? (
                  <FaTimes className="text-xl text-gray-400" />
                ) : (
                  <FaBars className="text-xl text-gray-400" />
                )}
              </button>
            </div>
          </div>
          {/* Bottom Border - Complex Gradient (inverse of top) */}
          <div
            className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${bottomBorderGradient} animate-pulse`}
          ></div>
        </div>
        {/* Mobile Premium Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-800 bg-gradient-to-b from-gray-900 to-black relative">
            {/* Mobile left border */}
            <div
              className={`absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b ${sideBorderGradient} animate-pulse`}
            ></div>
            {/* Mobile right border */}
            <div
              className={`absolute top-0 right-0 w-1.5 h-full bg-gradient-to-b ${sideBorderGradient} animate-pulse`}
            ></div>
            {/* Mobile bottom border */}
            <div
              className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${bottomBorderGradient} animate-pulse`}
            ></div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all duration-300 relative group ${
                      isActive(item.path)
                        ? `bg-gray-900/80 border-green-500/40 text-green-400 shadow-[0_0_20px_rgba(52,211,153,0.4)]`
                        : `bg-gray-900/40 border-gray-800 text-gray-400 hover:border-green-500 hover:text-green-300 hover:shadow-[0_0_25px_rgba(52,211,153,0.3)]`
                    }`}
                  >
                    {/* Complex gradient background on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/0 via-emerald-900/5 to-cyan-900/0 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300"></div>
                    <span className="text-2xl mb-2 relative z-10">
                      {item.icon}
                    </span>
                    <span className="text-sm font-medium relative z-10">
                      {item.label}
                    </span>
                    {/* Complex gradient border effect for active mobile buttons */}
                    {isActive(item.path) && (
                      <div className="absolute -inset-0.5 border border-gradient-to-r from-green-400/30 via-emerald-400/40 to-cyan-400/30 rounded-xl animate-pulse"></div>
                    )}
                  </button>
                ))}
                {adminItem && (
                  <button
                    onClick={() => handleNavigation(adminItem.path)}
                    className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all duration-300 relative group ${
                      isActive(adminItem.path)
                        ? `bg-gray-900/80 border-green-500/40 text-green-400 shadow-[0_0_20px_rgba(52,211,153,0.4)]`
                        : `bg-gray-900/40 border-gray-800 text-gray-400 hover:border-green-500 hover:text-green-300 hover:shadow-[0_0_25px_rgba(52,211,153,0.3)]`
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/0 via-emerald-900/5 to-cyan-900/0 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300"></div>
                    <span className="text-2xl mb-2 relative z-10">
                      {adminItem.icon}
                    </span>
                    <span className="text-sm font-medium relative z-10">
                      {adminItem.label}
                    </span>
                    {isActive(adminItem.path) && (
                      <div className="absolute -inset-0.5 border border-gradient-to-r from-green-400/30 via-emerald-400/40 to-cyan-400/30 rounded-xl animate-pulse"></div>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
export default Header;
